import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser, SignInButton } from '@clerk/clerk-react'
import { Shield, Zap, Database, Cpu, TrendingUp, Layers, ArrowUp, ArrowDown } from 'lucide-react'
import LandingHeader from '../components/LandingHeader'

// Valores agregados del producto para el carousel
const productValues = [
  { title: "SERVERLESS IDENTITY", icon: <Shield size={20} /> },
  { title: "UNIVERSAL BRIDGE", icon: <Zap size={20} /> },
  { title: "CLOUD STORAGE", icon: <Database size={20} /> },
  { title: "ONE-LINE INTEGRATION", icon: <Cpu size={20} /> },
]

// Duplicar valores para loop infinito
const carouselValues = [...productValues, ...productValues, ...productValues]

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  desc: string
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, desc }) => (
  <div className="bg-black p-8 group hover:bg-zinc-900 transition-colors border border-white/10">
    <div className="mb-6 text-zinc-300 group-hover:text-white transition-colors">{icon}</div>
    <h3 className="text-lg font-bold mb-3 uppercase tracking-wider text-white">{title}</h3>
    <p className="text-sm text-zinc-300 leading-relaxed">{desc}</p>
  </div>
)

export default function Home() {
  const { user, isLoaded } = useUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoaded && user) {
      navigate('/dashboard')
    }
  }, [user, isLoaded, navigate])

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black text-white">
        <style>{`
          @keyframes retro-scan {
            0% { transform: translateX(-40%); }
            100% { transform: translateX(140%); }
          }
        `}</style>
        <div className="space-y-6 text-center">
          <div className="w-24 h-8 border border-white/30 bg-black/40 overflow-hidden mx-auto">
            <div className="h-full w-1/2 bg-white/80" style={{ animation: 'retro-scan 1.4s linear infinite' }} />
          </div>
          <p className="text-zinc-300 text-sm font-semibold tracking-[0.2em] uppercase">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <LandingHeader />
      <div className="relative overflow-x-hidden bg-black text-white isolate">
      {/* CSS para Carousel Infinito */}
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(-50%)); }
        }
        .animate-scroll {
          display: flex;
          width: max-content;
          animation: scroll 35s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Hero Section */}
      <section className="px-6 max-w-7xl mx-auto flex flex-col justify-center min-h-screen">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-1">
            <div className="inline-block px-3 py-1 border border-white/20 text-[10px] uppercase tracking-[0.32em] text-zinc-300 mb-8">
              Universal Web3 Platform v1.0
            </div>
            <h1 className="text-8xl md:text-[10rem] lg:text-[11rem] font-black tracking-[-0.05em] mb-8 leading-[0.85]">
              <span className="text-white">
                SERVERLESS <br /> W3 SDK.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-100 max-w-2xl font-medium leading-relaxed mb-12">
              Unlock the full potential of Web3 with a single line of code. 
              OTD Core provides social login, encrypted cloud storage, and high-speed execution—completely serverless.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <a
                href="https://openthedoorz.gitbook.io/open-the-doorz/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-black text-[11px] font-black uppercase tracking-[0.22em] hover:bg-zinc-200 transition-all flex items-center gap-2"
              >
                VIEW DOCUMENTATION
              </a>
              <a
                href="https://github.com/reflecterlabs/OTD-Core"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-white/20 text-white text-[11px] font-black uppercase tracking-[0.22em] hover:bg-white/5 transition-all flex items-center gap-2"
              >
                GITHUB REPO
              </a>
            </div>

            {/* Values Carousel */}
            <div className="w-full overflow-hidden border-t border-white/10 pt-8">
              <div className="mb-4">
                <span className="text-[9px] uppercase tracking-[0.62em] text-zinc-400 font-bold">CORE FEATURES</span>
              </div>
              
              <div className="relative">
                {/* Masking Gradients */}
                <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none"></div>
                <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none"></div>
                
                <div className="animate-scroll">
                  {carouselValues.map((value, index) => (
                    <div 
                      key={index} 
                      className="relative group px-6 flex items-center gap-3 min-w-[280px] border border-white/10 bg-zinc-950/50 py-4 mr-3"
                    >
                      <div className="text-zinc-300 group-hover:text-white transition-colors">
                        {value.icon}
                      </div>
                      <span className="text-[10px] font-bold tracking-[0.2em] text-zinc-300 group-hover:text-white transition-colors">
                        {value.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Preview */}
          <div className="relative order-2 hidden lg:block">
            <div className="absolute -inset-20 bg-white/5 blur-[120px] rounded-full pointer-events-none"></div>
            <div className="relative z-10 border border-white/10 bg-black w-full max-w-[480px] mx-auto shadow-[0_0_100px_rgba(255,255,255,0.05)] lg:rotate-1 hover:rotate-0 transition-transform duration-700">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-white text-black flex items-center justify-center font-bold text-xs">OTD</div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-300">Preview Mode</span>
                </div>
              </div>
              <div className="p-6">
                <div className="bg-zinc-900/40 border border-white/10 mb-4 flex flex-col relative overflow-hidden">
                  <div className="p-4">
                    <div className="text-[8px] uppercase text-zinc-300 font-bold tracking-widest mb-1">Active Wallet</div>
                    <div className="font-mono text-[10px] text-zinc-400 mb-2 tracking-tighter">0x0471...952a</div>
                    <div className="text-2xl font-extrabold tracking-tighter text-white">$5,352.60</div>
                  </div>
                  <div className="grid grid-cols-2 border-t border-white/10 bg-white/5">
                    <div className="py-2.5 flex items-center justify-center gap-1.5 border-r border-white/10 text-[8px] uppercase font-bold tracking-widest text-zinc-300">
                      <ArrowUp size={10} /> Send
                    </div>
                    <div className="py-2.5 flex items-center justify-center gap-1.5 text-[8px] uppercase font-bold tracking-widest text-zinc-300">
                      <ArrowDown size={10} /> Receive
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1.5 mb-6">
                  {['Storage', 'Lending', 'Staging'].map((label, idx) => (
                    <div key={label} className="flex flex-col items-center gap-1 p-2 border border-white/5 bg-zinc-950/50">
                      {idx === 0 ? <Database size={12} className="text-zinc-300" /> : idx === 1 ? <TrendingUp size={12} className="text-zinc-300" /> : <Layers size={12} className="text-zinc-300" />}
                      <span className="text-[7px] uppercase font-bold tracking-widest text-zinc-300">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-6 max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-12 mb-16">
        <FeatureCard 
          icon={<Shield size={24} />} 
          title="Serverless Identity" 
          desc="Drop-in social login and non-custodial wallet creation using secure email/password auth protocols."
        />
        <FeatureCard 
          icon={<Zap size={24} />} 
          title="Universal Bridge" 
          desc="Low-latency transaction execution and cross-chain bridging capabilities built directly into the SDK."
        />
        <FeatureCard 
          icon={<Database size={24} />} 
          title="Cloud Storage" 
          desc="Encrypted user data and session management powered by Firebase for a persistent Web3 profile."
        />
        <FeatureCard 
          icon={<Cpu size={24} />} 
          title="One-Line Integration" 
          desc="A comprehensive developer experience designed for high-performance applications and clean stacks."
        />
      </section>
      </div>
    </>
  )
}
